import { ActionTypes, ProgramTypes } from '../ChannelArchitecture.d';
import { reconstructBlockName } from '../utils/architecture.utils';

const buildProgrammesByBlockName = (programmes) => {
  const programmesMap = new Map();
  programmes.forEach((programme) => {
    const blockKey = reconstructBlockName(programme.blockName);
    if (!programmesMap.has(blockKey)) programmesMap.set(blockKey, programme);
  });
  return programmesMap;
};

const getBlockReferenceData = (programme, programmesByBlockName) => {
  if (!programme.blockReference) return null;
  const reference = programmesByBlockName.get(reconstructBlockName(programme.blockReference));
  if (!reference) return null;

  return {
    titleGroup: reference.titleGroupName ?? null,
    series: reference.seriesName ?? null,
    type: reference.type ?? null,
    blockName: reference.blockName ?? null,
  };
};

// Resolve block reference data for each programme based on its block reference
const attachBlockReferenceData = (programmes) => {
  const programmesByBlockName = buildProgrammesByBlockName(programmes);
  return programmes.map((programme) => ({
    ...programme,
    blockReferenceData: getBlockReferenceData(programme, programmesByBlockName),
  }));
};

const baseSchedulesReducer = (state, { actionType, payload, saveChange }) => {
  switch (actionType) {
    case ActionTypes.CREATE:
      return {
        ...state,
        previous:
          state.previous.length == 0
            ? [{ item: [...state.programmes] }]
            : [...state.previous, { item: [...state.programmes] }],
        programmes: [...state.programmes, ...payload.item],
      };

    case ActionTypes.UPDATE: {
      const itemsById = new Map(payload.items.map((item) => [item.id, item]));
      const updated = state.programmes.map((item) => {
        return itemsById.has(item.id) ? { ...itemsById.get(item.id) } : item;
      });
      return {
        ...state,
        previous: [...state.previous, { item: [...state.programmes] }],
        programmes: updated,
      };
    }

    case ActionTypes.DELETE: {
      const { idsToDelete, itemsToUpdate } = payload;
      const itemsById = new Map(itemsToUpdate.map((item) => [item.id, item]));

      const programmes = state.programmes
        .filter((item) => !idsToDelete.includes(item.id))
        .map((item) => (itemsById.has(item.id) ? { ...itemsById.get(item.id) } : item));

      return {
        ...state,
        previous: [...state.previous, { item: [...state.programmes] }],
        programmes,
      };
    }

    case ActionTypes.UPDATEALL:
      return {
        previous: !state.previous ? [] : saveChange ? [...state.previous, { item: [...state.programmes], action: 'UPDATEALL' }] : [...state.previous],
        programmes: payload.programmes,
      };
    case ActionTypes.UNDO:
      return {
        ...state,
        programmes: payload?.previous ? state.programmes : state.previous[state.previous.length - 1].item,
        previous: payload?.previous ? payload.previous : state.previous.slice(0, -1),
      };

    case ActionTypes.CLEARHISTORY:
      return { ...state, previous: [] };

    default:
      return state;
  }
};

export function schedulesReducer(state, action) {
  const nextState = baseSchedulesReducer(state, action);
  if (nextState === state) return state;
  return { ...nextState, programmes: attachBlockReferenceData(nextState.programmes) };
}

export const cAModalReducer = (state, payload) => {
  const action = payload.action;
  if (action === 'changeValue') {
    const newValues = payload.newValues;
    return {
      ...state,
      ...newValues,
    };
  } else {
    const type = payload.type;
    const disablePlacement =
      payload.disablePlacement ??
      [ProgramTypes.REPEAT, ProgramTypes.MOVIES, ProgramTypes.PLACEHOLDER].includes(type);
    switch (type) {
      case 'Movies':
        return {
          ...state,
          type: type,
          disableMaxCount: false,
          disableSeason: true,
          disableSeries: true,
          disablePlacement,
          disableTitleGroup: true,
          disableLayout: false,
          disableSequential: false,
          disableStartTime: false,
          disableLinkReference: state.disableLinkToggle,
          disableType: false,
          disableReccurrence: false,
          disableDayOfWeek: false,
          disableEndTime: false,
          disableLinkToggle: false,
          layout: payload.layout !== undefined ? payload.layout : state.layout,
          sequential: false,
          series: null,
          seasons: null,
          titleGroup: null,
        };

      case 'Placeholder':
        return {
          ...state,
          type: type,
          disableMaxCount: false,
          disableSeason: true,
          disableSeries: true,
          disablePlacement,
          disableTitleGroup: true,
          disableLayout: false,
          disableSequential: false,
          sequential: false,
          disableStartTime: false,
          disableLinkReference: state.disableLinkToggle,
          disableType: false,
          disableReccurrence: false,
          disableDayOfWeek: false,
          disableEndTime: false,
          disableLinkToggle: false,
          layout: payload.layout !== undefined ? payload.layout : state.layout,
          series: null,
          seasons: null,
          titleGroup: null,
        };

      case 'Series':
        return {
          ...state,
          type: type,
          disableMaxCount: false,
          disableSeason: false,
          disableSeries: false,
          disablePlacement,
          disableTitleGroup: true,
          disableLayout: false,
          disableSequential: false,
          disableStartTime: false,
          disableLinkReference: state.disableLinkToggle,
          disableType: false,
          disableReccurrence: false,
          disableDayOfWeek: state.series ? true : false,
          disableEndTime: false,
          disableLinkToggle: false,
          layout: payload.layout !== undefined ? payload.layout : state.layout,
          titleGroup: null,
        };

      case 'TitleGroup':
      case 'Title Group':
        return {
          ...state,
          type: type,
          disableMaxCount: false,
          disableSeason: true,
          disableSeries: true,
          disablePlacement,
          disableTitleGroup: false,
          disableLayout: false,
          disableSequential: false,
          disableStartTime: false,
          disableLinkReference: state.disableLinkToggle,
          disableType: false,
          disableReccurrence: false,
          disableDayOfWeek: state.titleGroup ? true : false,
          disableEndTime: false,
          disableLinkToggle: false,
          layout: payload.layout !== undefined ? payload.layout : state.layout,
          series: null,
          seasons: null,
        };

      case 'Repeat':
        return {
          ...state,
          type: type,
          disableMaxCount: false,
          disableSeason: state.seasons?.id ? false : true,
          disableSeries: state.series?.id ? false : true,
          disablePlacement,
          disableTitleGroup: state.titleGroup || state.titleGroupID ? false : true,
          disableLayout: false,
          disableSequential: false,
          disableStartTime: false,
          disableLinkReference: state.disableLinkToggle,
          disableType: false,
          disableReccurrence: false,
          disableDayOfWeek: false,
          disableEndTime: false,
          disableLinkToggle: false,
          layout: payload.layout !== undefined ? payload.layout : state.layout,
        };

      default:
        return {
          ...state,
          disableMaxCount: false,
          disableSeries: false,
          disableTitleGroup: false,
          disableLayout: false,
          disableSequential: false,
          disableLinkReference: state.disableLinkToggle,
          disablePlacement,
          disableType: false,
          disableReccurrence: false,
          disableDayOfWeek: false,
          disableEndTime: false,
          disableLinkToggle: false,
          layout: payload.layout !== undefined ? payload.layout : state.layout,
        };
    }
  }
};
